<template>
  <div class="debtors-card">
    <div class="debtors-card__head">
      <div class="debtors-card__title">{{ $t('debtorsTitle') }}</div>
      <button
        type="button"
        class="debtors-card__all"
        @click="goAll"
      >
        {{ $t('viewAll') }}
        <v-icon x-small>mdi-chevron-left</v-icon>
      </button>
    </div>
    <div
      v-if="canSee"
      ref="scrollContainer"
      class="debtors-card__list"
    >
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        class="debtors-card__row"
        @click="edit(item)"
      >
        <span
          v-if="item.avatar && item.avatar.link"
          class="debtors-card__avatar"
        >
          <img :src="item.avatar.link" alt="">
        </span>
        <span
          v-else
          class="debtors-card__avatar debtors-card__avatar--fallback"
        >
          {{ initials(item.name) }}
        </span>
        <span class="debtors-card__meta">
          <span class="debtors-card__name">{{ item.name }}</span>
          <span class="debtors-card__id">#{{ item.user_id || item.id }}</span>
        </span>
        <span class="debtors-card__amount">
          {{ $numberFormat(item.remain && item.remain.remain) }}
          {{ $store.state.currency.name }}
        </span>
      </button>
      <div
        v-if="!loading && !items.length"
        class="debtors-card__empty"
      >
        <home-empty-state :size="80" />
      </div>
      <div
        v-if="loading || loadingPaginate"
        class="debtors-card__more"
      >
        <v-progress-circular
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
      </div>
    </div>
    <div
      v-else
      class="debtors-card__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

export default {
  name: 'HomeDebtorsCard',
  components: { HomeEmptyState },
  data () {
    return {
      items: [],
      loading: false,
      loadingPaginate: false,
      page: 1,
      lastPage: 1
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    }
  },
  mounted () {
    this.fetchList()
    this.$nextTick(() => {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll)
      }
    })
  },
  beforeDestroy () {
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    initials (name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '—'
      if (parts.length === 1) return parts[0].slice(0, 2)
      return parts[0].charAt(0) + parts[1].charAt(0)
    },
    fetchList () {
      if (!this.items.length) this.loading = true
      this.$axios.get(this.$endpoint + 'customer/show', {
        params: {
          page: this.page,
          options: JSON.stringify({ page: this.page, itemsPerPage: 20 }),
          status: 'active',
          remainStatus: '>'
        }
      }).then(r => {
        const newData = (r.data.data || []).filter(item => {
          return item.remain && item.remain.remain != 0 && item.remain.remain > 0
        })
        this.items = this.items.concat(newData)
        this.lastPage = r.data.last_page || 1
      }).catch(() => {
        if (!this.items.length) this.items = []
      }).finally(() => {
        this.loading = false
        this.loadingPaginate = false
      })
    },
    handleScroll () {
      const container = this.$refs.scrollContainer
      if (!container || this.loadingPaginate) return
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 8) {
        if (this.page < this.lastPage) {
          this.loadingPaginate = true
          this.page += 1
          this.fetchList()
        }
      }
    },
    goAll () {
      this.$router.push('/debtor/list').catch(() => {})
    },
    edit (item) {
      this.$router.push({ name: 'customerEdit', params: { id: item.id } }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.debtors-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px;
  height: 299px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.debtors-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.debtors-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.debtors-card__all {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: none;
  color: #0066FF;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}
.debtors-card__all .v-icon {
  color: #0066FF !important;
}
.debtors-card__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.debtors-card__list::-webkit-scrollbar {
  width: 4px;
}
.debtors-card__list::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 4px;
}
.debtors-card__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border: none;
  border-bottom: 1px solid #F3F4F6;
  background: none;
  cursor: pointer;
  text-align: start;
}
.debtors-card__row:last-child {
  border-bottom: none;
}
.debtors-card__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #F3F4F6;
}
.debtors-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.debtors-card__avatar--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #6B7280;
}
.debtors-card__meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.debtors-card__name {
  font-size: 12px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.debtors-card__id {
  font-size: 11px;
  color: #9CA3AF;
}
.debtors-card__amount {
  flex-shrink: 0;
  font-size: 12px;
  color: #111;
  white-space: nowrap;
}
.debtors-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
  min-height: 180px;
}
.debtors-card__more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
}
</style>
